import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BRAND } from '../brand';
import { explorerAddress } from '../data/tx';
import { shortAddr } from '../lib/format';
import { linkTo, navigate, useRoute } from '../router';
import { useWallet } from '../wallet/WalletProvider';
import { BrandMark, IconCheck, IconChevronDown, IconCopy, IconExternal, IconGear, IconHistory, IconLogout, IconSpinner } from './Icons';
import { useModals } from './modals';

const NAV = [
  { to: '/', name: 'dashboard', label: 'Dashboard' },
  { to: '/markets', name: 'markets', label: 'Markets' },
  { to: '/faucet', name: 'faucet', label: 'Faucet' },
  { to: '/status', name: 'status', label: 'Status' },
] as const;

/** Popover that closes on outside click and Escape. */
function Menu({ button, children, label }: { button: (open: boolean, toggle: () => void) => ReactNode; children: (close: () => void) => ReactNode; label: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => { if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', close); };
  }, [open]);
  return (
    <div className="menu" ref={box}>
      {button(open, () => setOpen((v) => !v))}
      {open && <div className="menu__panel" role="menu" aria-label={label}>{children(() => setOpen(false))}</div>}
    </div>
  );
}

/** Deterministic two-tone avatar from the address: recognisable at a glance without any external service. */
function Avatar({ address }: { address: string }) {
  const a = parseInt(address.slice(2, 8), 16) % 360, b = parseInt(address.slice(-6), 16) % 360;
  return <span className="avatar" aria-hidden="true" style={{ background: `linear-gradient(135deg, oklch(0.72 0.13 ${a}), oklch(0.55 0.16 ${b}))` }} />;
}

function WalletWidget() {
  const { status, address, walletLabel, disconnect } = useWallet();
  const { openConnect } = useModals();
  const [copied, setCopied] = useState(false);
  if (status !== 'connected' || !address) {
    return <button type="button" className="btn btn--gradient wallet-connect" onClick={openConnect} disabled={status === 'connecting'} aria-label={status === 'connecting' ? 'Connecting…' : 'Connect wallet'}>
      {status === 'connecting' && <IconSpinner />}
      <span className="wallet-connect__full">{status === 'connecting' ? 'Connecting…' : 'Connect wallet'}</span>
      <span className="wallet-connect__compact" aria-hidden="true">{status === 'connecting' ? 'Connecting…' : 'Connect'}</span>
    </button>;
  }
  return (
    <Menu label="Wallet"
      button={(open, toggle) => (
        <button type="button" className="btn btn--header" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
          <Avatar address={address} /><span className="mono">{shortAddr(address)}</span><IconChevronDown size={14} />
        </button>
      )}>
      {(close) => (
        <>
          <div className="menu__account"><Avatar address={address} /><div><strong className="mono">{shortAddr(address)}</strong>{walletLabel && <small title={walletLabel}>{walletLabel}</small>}</div></div>
          <div className="menu__network"><small>Network</small><span><i className="dot" />Robinhood Chain Testnet</span></div>
          <button type="button" role="menuitem" onClick={() => { close(); navigate('/history'); }}><IconHistory />Transaction history</button>
          <button type="button" role="menuitem" onClick={() => { void navigator.clipboard?.writeText(address).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}>{copied ? <IconCheck /> : <IconCopy />}{copied ? 'Copied' : 'Copy address'}</button>
          <a role="menuitem" href={explorerAddress(address)} target="_blank" rel="noopener noreferrer"><IconExternal />View on Explorer</a>
          <button type="button" role="menuitem" onClick={() => { close(); disconnect(); navigate('/markets'); }}><IconLogout />Disconnect</button>
        </>
      )}
    </Menu>
  );
}

/**
 * True while the visitor scrolls down past the top of the page, false as soon as they scroll back up. Phones use it to
 * slide the two-row header away (styles.css); small movements accumulate until they pass the threshold.
 */
function useScrolledDown(): boolean {
  const [down, setDown] = useState(false);
  useEffect(() => {
    let last = window.scrollY, raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        if (y < 120) { setDown(false); last = y; return; }
        if (Math.abs(y - last) < 8) return;
        setDown(y > last);
        last = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);
  return down;
}

export function AppHeader({ theme, onToggleTheme }: { theme: 'light' | 'dark'; onToggleTheme(): void }) {
  const route = useRoute();
  const active = route.name === 'reserve' ? 'markets' : route.name;
  const scrolledDown = useScrolledDown();
  return (
    <header className="header" data-scrolled-down={scrolledDown || undefined}>
      <div className="header__inner">
        <a className="brand" {...linkTo('/')}><BrandMark /><span className="brand__name">{BRAND.name}</span><span className="pill pill--testnet">Testnet</span></a>
        <nav className="nav" aria-label="Primary">
          {NAV.map((n) => <a key={n.to} {...linkTo(n.to)} aria-current={active === n.name ? 'page' : undefined}>{n.label}</a>)}
        </nav>
        <div className="header__tools">
          <WalletWidget />
          <Menu label="Settings"
            button={(open, toggle) => <button type="button" className="icon-btn icon-btn--header" aria-label="Settings" aria-haspopup="menu" aria-expanded={open} onClick={toggle}><IconGear size={18} /></button>}>
            {() => (
              <>
                <p className="menu__title">Global settings</p>
                <div className="menu__row"><span>Dark mode</span>
                  <button type="button" role="switch" className="switch" aria-checked={theme === 'dark'} aria-label="Dark mode" onClick={onToggleTheme}><span /></button>
                </div>
                <div className="menu__row"><span>Testnet mode</span><span className="pill pill--testnet">On</span></div>
              </>
            )}
          </Menu>
        </div>
      </div>
    </header>
  );
}
